export const CLASSIC_TICKET_DURATION_MS = 60_000;
/** Deliberately small command surface: never accept an athlete row from a phone. */
export const ENTRY_HEIGHTS = [
  "hauteurSquat",
  "hauteurDc",
  "hauteurSecu",
  "positionRack",
  "cales",
] as const;
/** Numeric wire values keep equipment choices within the same validated command surface. */
export const ENTRY_RACK_POSITIONS = ["EXT", "INT", "1/2 D", "1/2 G"] as const;
export const isEntryEquipmentField = (field: string) =>
  ENTRY_HEIGHTS.includes(field as (typeof ENTRY_HEIGHTS)[number]);
/** Receipts retain option labels so later profile edits do not rename historical choices. */
export function entryDisplayValue(
  field: string,
  value: number | null | undefined,
  label?: string,
): string {
  return (
    label ??
    (field === "positionRack" && value != null
      ? ENTRY_RACK_POSITIONS[value]
      : undefined) ??
    String(value ?? "-")
  );
}
export const ENTRY_LIFTS = [
  "squat1",
  "squat2",
  "squat3",
  "bench1",
  "bench2",
  "bench3",
  "deadlift1",
  "deadlift2",
  "deadlift3",
] as const;
export type EntryField =
  | (typeof ENTRY_HEIGHTS)[number]
  | (typeof ENTRY_LIFTS)[number]
  | `custom:${string}`;
export interface EntryCell {
  /** Secretary refusal closes mobile entry for this attempt. */
  entryRefused?: boolean;
  reviewRequired?: boolean;
  reviewReason?: "third-attempt-change" | "custom-attempt-change";
  field: EntryField;
  label?: string;
  unit?: string;
  kind?: "height" | "attempt";
  options?: { value: number; label: string }[];
  /** Host-authorized record exception, separate from the ordinary weight grid. */
  record?: { step: number; min: number };
  ticketReference?: string;
  ticketCompleted?: boolean;
  next?: boolean;
  value: number | null;
  version: string;
  editable: boolean;
  min: number;
  max: number;
  step: number;
  ticket?: { id: string; deadline: number; uncertainty: number };
}
export interface EntryAthlete {
  id: string;
  name: string;
  groupId: string;
  groupName: string;
  /** Announced passages in this group's current movement; null if unknown. */
  passagesBefore?: number | null;
  phase?: "heights" | "attempts";
  attemptState?:
    | "waiting"
    | "submitted"
    | "on-bar"
    | "finished"
    | "unavailable";
  cells: EntryCell[];
}
export interface EntrySnapshot {
  meetId: string;
  name: string;
  enabled: boolean;
  epoch: string;
  expiresAt: number;
  athletes: EntryAthlete[];
}
export interface EntryUpdate {
  field: EntryField;
  value: number;
  version: string;
}
/** Static drawing only: normalized coordinates, no pressure or gesture timing. */
export type EntrySignatureDrawing = [number, number][][];
export interface EntryTicketSignature {
  drawing: EntrySignatureDrawing;
  capturedAt: number;
  confirmedAt: number;
  sessionId: string;
}
export function isEntrySignatureDrawing(
  value: unknown,
): value is EntrySignatureDrawing {
  if (!Array.isArray(value) || !value.length || value.length > 100)
    return false;
  let points = 0;
  let ink = false;
  for (const stroke of value) {
    if (!Array.isArray(stroke) || stroke.length < 2) return false;
    points += stroke.length;
    if (points > 3000) return false;
    for (const point of stroke) {
      if (
        !Array.isArray(point) ||
        point.length !== 2 ||
        point.some(
          (v) =>
            typeof v !== "number" || !Number.isFinite(v) || v < 0 || v > 1000,
        )
      )
        return false;
      if (Math.hypot(point[0] - stroke[0][0], point[1] - stroke[0][1]) >= 10)
        ink = true;
    }
  }
  return ink;
}
/** Only validated numeric coordinates are interpolated into the preview. */
export function entrySignatureImage(
  drawing: EntrySignatureDrawing,
): string | undefined {
  if (!isEntrySignatureDrawing(drawing)) return undefined;
  const paths = drawing
    .map(
      (stroke) =>
        `<polyline points="${stroke.map(([x, y]) => `${x},${y}`).join(" ")}"/>`,
    )
    .join("");
  return `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000" preserveAspectRatio="none"><g fill="none" stroke="#111" stroke-width="5" stroke-linecap="round" stroke-linejoin="round">${paths}</g></svg>`)}`;
}
export interface EntryRequest extends EntryUpdate {
  confirmSignature?: true;
  signatureDrawing?: EntrySignatureDrawing;
  id: string;
  /** Additional equipment fields only; the primary field is never repeated. */
  updates?: EntryUpdate[];
}
export interface EntryReceiptUpdate {
  field: EntryField;
  label?: string;
  value: number;
  valueLabel?: string;
  previousValue?: number | null;
  previousValueLabel?: string;
}
export interface EntryCommand extends EntryRequest {
  signature?: EntryTicketSignature;
  reviewRequired?: boolean;
  reviewReason?: "third-attempt-change" | "custom-attempt-change";
  meetId: string;
  athleteId: string;
  sessionId: string;
  epoch: string;
  receivedAt: number;
  /** Internal host clock conversion; never part of the mobile request. */
  hostClockOffset?: number;
  timing: "on-time" | "late" | "unknown" | "none";
  lateByMs: number | null;
  ticketDeadline: number | null;
  label?: string;
}
export interface EntryReceipt {
  signature?: EntryTicketSignature;
  /** All equipment values saved atomically, including the primary field. */
  updates?: EntryReceiptUpdate[];
  reviewRequired?: boolean;
  reviewReason?: "third-attempt-change" | "custom-attempt-change";
  superseded?: boolean;
  restoreApplied?: boolean;
  athleteName?: string;
  decision?: "accepted" | "refused";
  /** Value actually retained at the time of the secretary's decision. */
  retainedValue?: number | null;
  retainedValueLabel?: string;
  previousValue?: number | null;
  valueLabel?: string;
  previousValueLabel?: string;
  reviewedAt?: string;
  reviewNote?: string;
  label?: string;
  id: string;
  athleteId: string;
  field: EntryField;
  value: number;
  status: "applied" | "rejected";
  reason?: string;
  appliedAt: number;
  timing: EntryCommand["timing"];
  lateByMs: number | null;
  receivedAt: number;
  ticketDeadline: number | null;
}
export function normalizeEntryCode(value: unknown): string {
  return typeof value === "string"
    ? value.replace(/\s/g, "").toUpperCase()
    : "";
}
export function validateEntryRequest(value: unknown): EntryRequest {
  const r = value as EntryRequest;
  const validUpdate = (u: EntryUpdate) =>
    !!u &&
    ([...ENTRY_HEIGHTS, ...ENTRY_LIFTS].includes(u.field as never) ||
      (typeof u.field === "string" && /^custom:[\w-]{1,100}$/.test(u.field))) &&
    typeof u.value === "number" &&
    Number.isFinite(u.value) &&
    typeof u.version === "string" &&
    /^[\da-f]{64}$/.test(u.version);
  if (
    !r ||
    typeof r !== "object" ||
    Array.isArray(r) ||
    ("confirmSignature" in r && r.confirmSignature !== true) ||
    ("signatureDrawing" in r &&
      (!r.confirmSignature || !isEntrySignatureDrawing(r.signatureDrawing))) ||
    !["field,id,value,version", "field,id,updates,value,version"].includes(
      Object.keys(r)
        .filter(
          (key) => !["confirmSignature", "signatureDrawing"].includes(key),
        )
        .sort()
        .join(","),
    ) ||
    !/^[\da-f]{8}-[\da-f-]{27}$/i.test(r.id) ||
    !validUpdate(r) ||
    ("updates" in r &&
      (!Array.isArray(r.updates) ||
        r.updates.length > 99 ||
        r.updates.some(
          (u) =>
            Object.keys(u || {})
              .sort()
              .join(",") !== "field,value,version" || !validUpdate(u),
        ) ||
        new Set([r.field, ...r.updates.map((u) => u.field)]).size !==
          r.updates.length + 1))
  )
    throw Error("INVALID_REQUEST");
  return r;
}
export function validateEntryCell(
  cell: EntryCell | undefined,
  request: EntryRequest | EntryUpdate,
): void {
  if (!cell?.editable || cell.field !== request.field) throw Error("LOCKED");
  if (cell.version !== request.version) throw Error("CONFLICT");
  if (!isEntryValueAllowed(cell, request.value)) throw Error("INVALID_VALUE");
}
export const entryRequestUpdates = (r: EntryRequest): EntryUpdate[] => [
  { field: r.field, value: r.value, version: r.version },
  ...(r.updates || []),
];
/** A batch may only edit the host-authorized equipment surface, never attempts. */
export function validateEntryRequestCells(
  cells: EntryCell[],
  request: EntryRequest,
): EntryCell[] {
  validateEntryRequest({
    id: request.id,
    field: request.field,
    value: request.value,
    version: request.version,
    ...(request.updates !== undefined ? { updates: request.updates } : {}),
  });
  return entryRequestUpdates(request).map((update) => {
    const cell = cells.find((c) => c.field === update.field);
    if (
      request.updates !== undefined &&
      !(
        cell?.kind === "height" ||
        (cell?.kind !== "attempt" && isEntryEquipmentField(update.field))
      )
    )
      throw Error("INVALID_REQUEST");
    validateEntryCell(cell, update);
    return cell!;
  });
}
/** Used by the phone, relay and local transactional writer. */
export function isEntryValueAllowed(cell: EntryCell, value: number): boolean {
  if (cell.options && !cell.options.some((option) => option.value === value))
    return false;
  const grid = (step: number) =>
    Number.isFinite(step) &&
    step > 0 &&
    Math.abs(value / step - Math.round(value / step)) <= 1e-7;
  if (
    !Number.isFinite(value) ||
    !Number.isFinite(cell.min) ||
    !Number.isFinite(cell.max) ||
    value > cell.max
  )
    return false;
  return (
    (value >= cell.min && grid(cell.step)) ||
    (!!cell.record &&
      Number.isFinite(cell.record.min) &&
      value >= cell.record.min &&
      grid(cell.record.step))
  );
}

export function entryTiming(
  ticket: EntryCell["ticket"],
  receivedAt: number,
): Pick<EntryCommand, "timing" | "lateByMs" | "ticketDeadline"> {
  if (!ticket)
    return { timing: "none" as const, lateByMs: null, ticketDeadline: null };
  const delta = receivedAt - ticket.deadline;
  return {
    timing:
      Math.abs(delta) <= ticket.uncertainty
        ? ("unknown" as const)
        : delta > 0
          ? ("late" as const)
          : ("on-time" as const),
    lateByMs: Math.max(0, delta),
    ticketDeadline: ticket.deadline,
  };
}

export interface EntryOperatorTicket {
  id: string;
  athleteId: string;
  status: string;
  receipt?: EntryReceipt | null;
  request?: Partial<EntryCommand>;
  reviewedAt?: string | null;
  reviewNote?: string | null;
}
export function mergeEntryTickets(
  local: EntryReceipt[],
  remote: EntryOperatorTicket[],
): EntryOperatorTicket[] {
  const rows = new Map<string, EntryOperatorTicket>(
    local.map((r) => [
      r.id,
      {
        id: r.id,
        athleteId: r.athleteId,
        status: r.status,
        receipt: r,
        reviewedAt: r.reviewedAt,
        reviewNote: r.reviewNote,
      },
    ]),
  );
  for (const r of remote) {
    const old = rows.get(r.id);
    const receipt = old?.receipt?.decision
      ? old.receipt
      : r.receipt || old?.receipt;
    rows.set(r.id, {
      ...old,
      ...r,
      receipt,
      reviewedAt: receipt?.decision
        ? receipt.reviewedAt
        : r.reviewedAt || old?.reviewedAt,
      reviewNote: receipt?.decision
        ? receipt.reviewNote
        : (r.reviewNote ?? old?.reviewNote),
    });
  }
  return [...rows.values()].sort(
    (a, b) =>
      (b.receipt?.receivedAt || b.request?.receivedAt || 0) -
      (a.receipt?.receivedAt || a.request?.receivedAt || 0),
  );
}
export const entryTicketNeedsReview = (r: EntryOperatorTicket) =>
  (r.receipt?.status === "applied" ? !r.receipt.decision : !r.reviewedAt) &&
  !!r.receipt &&
  (r.receipt.reviewRequired || ["late", "unknown"].includes(r.receipt.timing));
